import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { Job } from "../lib/types/models";
import {
  jobCategoryDisplay,
  parseJobStatus,
  urgencyLabel,
} from "../lib/types/enums";
import { budgetRange, km, relative } from "../lib/format";
import { StatusPill } from "@ui/react";
import { ClockIcon, PinIcon } from "@ui/react/icons";

/** Status as a tonal pill that always carries its label, never colour alone.
 *  Tone and wording both come from the shared table — this screen has no
 *  opinion about what colour "scheduled" is. */
export function JobStatusPill({ status }: { status: string }) {
  const parsed = parseJobStatus(status);
  return <StatusPill kind="job" status={parsed} />;
}

/**
 * One job in a list.
 *
 * The card body is a single link and any actions live in `footer`, outside it —
 * a button nested inside an anchor is invalid HTML and produces a control that
 * keyboard users cannot reach separately from the link.
 */
export function JobCard({
  job,
  href,
  footer,
}: {
  job: Job;
  href: string;
  footer?: ReactNode;
}) {
  const urgency = urgencyLabel(job.urgency);
  const place = [job.location?.city, job.location?.province]
    .filter(Boolean)
    .join(", ");
  // `distance` only ever arrives alongside a masked (not-yet-mine) job — see
  // `Job.distance` — so "X km away" and the street address never both show.
  const distance = km(job.distance);
  const locationLine = [distance ? `${distance} away` : null, place].filter(Boolean).join(" · ");

  return (
    <div className="card card--dense card--flush">
      <Link to={href} className="row">
        <div className="stack stack--tight">
          <div className="row__top">
            <span className="row__title">{job.title}</span>
            <JobStatusPill status={job.status} />
          </div>

          {/* One tone for one kind of thing. `.label-sm` is already
              on-surface-variant; this row used to paint the category in it and
              urgency and the quote count a step darker, so three facts of equal
              standing read as a hierarchy that does not exist. `.dim` is
              reserved for the timestamp below. */}
          <div className="cluster">
            <span className="label-sm">{jobCategoryDisplay(job.category)}</span>
            {urgency ? <span className="label-sm">{urgency} urgency</span> : null}
            {job.quoteCount > 0 ? (
              <span className="label-sm">
                {job.quoteCount} quote{job.quoteCount === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>

          {job.description ? <p className="muted clamp-2">{job.description}</p> : null}

          <div className="cluster">
            {locationLine ? (
              <span className="cluster label-sm">
                <PinIcon className="icon-sm" />
                {locationLine}
              </span>
            ) : null}
            <span className="cluster label-sm dim">
              <ClockIcon className="icon-sm" />
              Posted {relative(job.createdAt)}
            </span>
            <span className="amount push-end">
              {budgetRange(job.budget?.min, job.budget?.max, job.budget?.currency ?? "ZAR")}
            </span>
          </div>
        </div>
      </Link>

      {footer ? (
        <div className="cluster job-card__footer">
          {footer}
        </div>
      ) : null}
    </div>
  );
}
